import httpService from "../../services/httpService";

export interface SongV2Source {
  type: "youtube";
  url: string;
  video_id: string;
}

export interface SongV2Lane {
  lane: number;
  name: string;
  midi_notes: number[];
}

export interface SongV2TempoMap {
  tick: number;
  bpm: number;
}

export interface SongV2ChartEvent {
  t0: number;
  dt: number;
  m: number;
}

export interface SongV2Chart {
  format: string;
  ppq: number;
  tempo_map: SongV2TempoMap[];
  events: SongV2ChartEvent[];
}

export interface SongV2 {
  _id: string;
  songv2_id: string;
  v: number;
  title: string;
  artist: string;
  bpm: number;
  ts: string;
  duration_ms: number;
  source: SongV2Source;
  sync_ms: number;
  lookahead_ms: number;
  hit_ms: number;
  calibrate_speed: number;
  lanes: SongV2Lane[];
  dt?: number[];
  m?: number[];
  chart?: SongV2Chart;
  is_locked: boolean;
  
  // Backend README'de belirtilen ek alanlar
  plays_count?: number;      // Total play count
  likes_count?: number;      // Total likes
  status?: "draft" | "published" | "archived";  // Song status
  difficulty?: "beginner" | "intermediate" | "advanced" | "expert";  // Difficulty level
  
  created_at: string;
  updated_at: string;
}

export interface CreateSongV2Data {
  v: number;
  title: string;
  artist: string;
  bpm: number;
  ts: string;
  duration_ms: number;
  source: SongV2Source;
  sync_ms: number;
  lookahead_ms: number;
  hit_ms: number;
  calibrate_speed: number;
  lanes: SongV2Lane[];
  dt: number[];
  m: number[];
  chart: SongV2Chart;
  is_locked?: boolean;
}

export interface UpdateSongV2Data {
  title?: string;
  artist?: string;
  bpm?: number;
  ts?: string;
  duration_ms?: number;
  source?: SongV2Source;
  sync_ms?: number;
  lookahead_ms?: number;
  hit_ms?: number;
  calibrate_speed?: number;
  lanes?: SongV2Lane[];
  dt?: number[];
  m?: number[];
  chart?: SongV2Chart;
  is_locked?: boolean;
}

export interface SongV2QueryParams {
  limit?: number;
  offset?: number;
  artist?: string;
  title?: string;
  q?: string;
}

export interface UserStats {
  user_id: string;
  total_assigned: number;
  unique_artists: number;
  avg_bpm: number;
  total_duration: number;
  total_duration_formatted: string;
  top_artists: Array<{
    artist: string;
    song_count: number;
  }>;
}

export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

export interface PaginatedResponse<T> {
  success: boolean;
  message: string;
  data: T[];
  total: number;
  limit: number;
  offset: number;
}

const SONGSV2_BASE = "/songsv2";

// Get all songs (without chart data)
export const fetchSongsV2 = async (params?: SongV2QueryParams): Promise<PaginatedResponse<SongV2>> => {
  const queryString = params ? "?" + new URLSearchParams(params as any).toString() : "";
  const response = await httpService.get<PaginatedResponse<SongV2>>(
    `${SONGSV2_BASE}${queryString}`
  );
  // httpService.get returns the full backend response already
  // Backend: { success, message, data: SongV2[], total, limit, offset }
  return response as any as PaginatedResponse<SongV2>;
};

// Get song by ID (with full chart data)
export const fetchSongV2ById = async (id: string): Promise<SongV2> => {
  const response = await httpService.get<ApiResponse<SongV2>>(
    `${SONGSV2_BASE}/${id}`
  );
  // Backend: { success, message, data: SongV2 }
  return (response as any).data;
};

// Create new song (admin only)
export const createSongV2 = async (data: CreateSongV2Data): Promise<SongV2> => {
  const response = await httpService.post<ApiResponse<SongV2>>(
    SONGSV2_BASE,
    data
  );
  // Backend: { success, message, data: SongV2 }
  return (response as any).data;
};

// Update song (admin only)
export const updateSongV2 = async (id: string, data: UpdateSongV2Data): Promise<SongV2> => {
  const response = await httpService.put<ApiResponse<SongV2>>(
    `${SONGSV2_BASE}/${id}`,
    data
  );
  // Backend: { success, message, data: SongV2 }
  return (response as any).data;
};

// Delete song (admin only)
export const deleteSongV2 = async (id: string): Promise<void> => {
  await httpService.delete(`${SONGSV2_BASE}/${id}`);
};

// Search songs
export const searchSongsV2 = async (query: string, params?: SongV2QueryParams): Promise<PaginatedResponse<SongV2>> => {
  const queryString = new URLSearchParams({
    q: query,
    ...(params as any),
  }).toString();
  const response = await httpService.get<PaginatedResponse<SongV2>>(
    `${SONGSV2_BASE}/search?${queryString}`
  );
  return response as any as PaginatedResponse<SongV2>;
};

// Get search suggestions
export const getSuggestionsV2 = async (query: string, limit = 5): Promise<string[]> => {
  const response = await httpService.get<ApiResponse<string[]>>(
    `${SONGSV2_BASE}/suggestions?q=${encodeURIComponent(query)}&limit=${limit}`
  );
  return (response as any).data;
};

// Get my assigned songs
export const fetchMySongsV2 = async (params?: SongV2QueryParams): Promise<PaginatedResponse<SongV2>> => {
  const queryString = params ? "?" + new URLSearchParams(params as any).toString() : "";
  const response = await httpService.get<PaginatedResponse<SongV2>>(
    `${SONGSV2_BASE}/my-songs${queryString}`
  );
  return response as any as PaginatedResponse<SongV2>;
};

// Get my song stats
export const fetchMyStatsV2 = async (): Promise<UserStats> => {
  const response = await httpService.get<ApiResponse<UserStats>>(
    `${SONGSV2_BASE}/my-stats`
  );
  return (response as any).data;
};

// Get assigned songs (detailed)
export const fetchAssignedSongsV2 = async (params?: SongV2QueryParams): Promise<any> => {
  const queryString = params ? "?" + new URLSearchParams(params as any).toString() : "";
  const response = await httpService.get<ApiResponse<any>>(
    `${SONGSV2_BASE}/assigned${queryString}`
  );
  return (response as any).data;
};

